import { type IconType } from '@assembly-js/design-system';
import { MenuItem } from '@mui/material';
import { FormikProps } from 'formik';
import { omit } from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { v4 } from 'uuid';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

import {
  APP_SETUP_PAGE,
  CLIENT_APPS_PAGE,
  VisibilityRuleType,
  WEBHOOK_EVENTS,
} from 'src/constants';
import { AppNotificationConfig } from 'src/constants/dataTypes/appsTypes';
import {
  PortalConfigContext,
  RouteContext,
  useFeatureFlags,
} from 'src/context';
import { AppSettingsModalContext } from 'src/features/app-settings-modal/AppSettingsModalContext';
import { SuggestIconPicker } from 'src/features/SuggestIconPicker';
import history from 'src/history';
import { AppVisibilityConfigSetting } from 'src/legacy/components/app-visibility/AppVisibilityConfigSetting';
import { DuplicateAppInstance } from 'src/legacy/components/AppSetup/DuplicateAppInstance';
import { RemoveApp } from 'src/legacy/components/AppSetup/RemoveApp';
import { ModalWrapper } from 'src/legacy/components/Modals';
import ComboBox from 'src/legacy/components/Select/ComboBox';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import { BaseTextField } from 'src/legacy/components/TextField';
import { ApiKey } from 'src/legacy/components/UI/ApiKey';
import {
  useCreateAppMutation,
  useGetAppQuery,
  useGetInstallsQuery,
  useUpdateAppMutation,
} from 'src/services/api/applicationsApi';
import {
  clientRuleSchema,
  companyRuleSchema,
  customFieldRuleSchema,
  VisibilityScenario,
} from 'src/utils/app_visibility';

import { ModuleContainer } from '../../ModuleContainer';
import { SettingsPageForm } from '../../SettingsPageForm';
import SettingsToggleCard from '../../SettingsToggleCard';
import { useAppSetupPaths } from '../useAppSetupPaths';

import { SetupModulePageQueryParams } from './appSetupTypes';
import { NotificationSettingsSection } from './NotificationSettingsSection';
import SetupAppBreadCrumbs from './SetupAppBreadCrumbs';
import { URLQueryParamsSection } from './URLQueryParamsSection';

import { SetupModulePageDescription } from '.';

const noVisibilityRuleSchema = z.object({
  ruleType: z.literal(VisibilityRuleType.NoVisibility),
});

const schema = z.object({
  name: z.string({ message: 'Name is required' }),
  // The design-system IconPicker returns FA/DS icon names outside the
  // legacy IconType enum. The picker will handle the real validation.
  icon: z
    .string()
    .min(1, 'Icon is required')
    // type-casting here to keep things strongly-typed in the form values and
    // avoid having to cast in multiple places
    .default('dashboard') as z.ZodType<IconType>,
  visibility: z.enum(['client', 'internal']),
  clientUrl: z.string().url('Client-facing URL is not a valid URL').optional(),
  internalUrl: z
    .string()
    .url('Internal-facing URL is not a valid URL')
    .optional(),
  notificationUrl: z
    .string()
    .url('Notification URL is not a valid URL')
    .optional(),
  enableWebhooks: z.boolean(),
  webhookUrl: z
    .string()
    .url('Webhook Endpoint URL is not a valid URL')
    .optional(),
  visibilityConfig: z.array(
    z.discriminatedUnion('ruleType', [
      clientRuleSchema,
      companyRuleSchema,
      customFieldRuleSchema,
      noVisibilityRuleSchema,
    ]),
  ),
  webhookEvents: z.array(z.enum(WEBHOOK_EVENTS)),
  queryParams: z
    .array(
      z.object({
        key: z.string().min(1, 'Key is required'),
        value: z.string().min(1, 'Value is required'),
      }),
    )
    .default([]),
  notificationConfig: z
    .object({
      notifications: z
        .array(
          z.object({
            // New settings carry an empty id (the backend stamps a stable UUID),
            // and Formik's validation coerces "" to undefined. Defaulting back to
            // "" keeps validation passing for freshly added settings (otherwise the
            // form's save button wrongly disables) while leaving id typed as string.
            id: z.string().default(''),
            label: z.string().min(1, 'Label is required'),
            surfaces: z
              .array(z.enum(['product', 'email']))
              .min(1, 'Select at least one surface'),
            default: z.object({
              product: z.boolean(),
              email: z.boolean(),
            }),
          }),
        )
        .default([]),
      // Fields are individually optional so a partially-typed action label never blocks the
      // form; handleSave persists it only when all three parts are filled.
      actionLabel: z
        .object({
          verb: z.string().optional(),
          singularNoun: z.string().optional(),
          pluralNoun: z.string().optional(),
        })
        .optional(),
    })
    .optional(),
});

type Schema = z.infer<typeof schema>;

const useStyles = makeStyles()((theme) => ({
  nameDescription: {
    display: 'inline-block',
  },

  section: {
    marginBottom: 40,
  },

  removeApp: {
    paddingTop: theme.spacing(3.5),
    marginBottom: 100,
  },

  heading: {
    marginBottom: 16,
  },

  field: {
    marginBottom: 32,
  },
}));

const ensureWebhookEvents = (
  events: string[] | undefined,
): (typeof WEBHOOK_EVENTS)[number][] => {
  type WebhookEvent = (typeof WEBHOOK_EVENTS)[number];
  const eventObj: Partial<Record<WebhookEvent, true>> = {};
  WEBHOOK_EVENTS?.forEach((event) => (eventObj[event] = true));
  return (
    events?.filter<WebhookEvent>(
      (event): event is WebhookEvent => event in eventObj,
    ) ?? []
  );
};

function initializeEmptyVisibilityConfig(): Schema['visibilityConfig'] {
  return [];
}

const useInitialValues = (appId: string, moduleId: string) => {
  const { data: { moduleSettings, extensionsSettings } = {} } =
    useGetInstallsQuery();
  const module = moduleSettings?.find((m) => m.id === moduleId);
  const extension = module ? extensionsSettings?.[module.id] : undefined;
  const { data: app, isSuccess } = useGetAppQuery(
    { id: appId ?? '' },
    { skip: !appId, refetchOnMountOrArgChange: true },
  );
  return {
    isSuccess: !appId
      ? true
      : isSuccess && Boolean(extension) && Boolean(module),
    initialFormValue: {
      name: extension?.name ?? '',
      icon: extension?.icon ?? ('dashboard' as IconType),
      visibility: app?.clientUrl ? 'client' : 'internal',
      clientUrl: app?.clientUrl ?? '',
      internalUrl: app?.internalUrl ?? '',
      notificationUrl: app?.notificationUrl ?? '',
      webhookUrl: app?.webhookUrl ?? '',
      webhookEvents: ensureWebhookEvents(app?.webhookEvents ?? []),
      enableWebhooks: Boolean(app?.webhookUrl),
      visibilityConfig: initializeEmptyVisibilityConfig(),
      queryParams: extension?.queryParams ?? [],
      notificationConfig: (app?.notificationConfig ?? {
        notifications: [],
      }) as AppNotificationConfig,
    } as const,
  };
};

const useForm = () => {
  const { t } = useTranslation();
  const { query } = React.useContext(RouteContext);
  const { moduleId } = query as SetupModulePageQueryParams;
  const { data: { extensionsSettings } = {} } = useGetInstallsQuery();
  const isAppBuilderApp = Boolean(extensionsSettings?.[moduleId]?.appBuilderId);

  const FormRenderer = ({
    values,
    errors,
    handleBlur,
    handleChange,
    touched,
    setFieldValue,
  }: FormikProps<Schema>) => {
    const { classes } = useStyles();
    const { EnableMarketplaceAppNotificationSettings } = useFeatureFlags();
    const isAddingNewApp = moduleId === 'new';

    return (
      <>
        <section className={classes.section}>
          <BaseTypography fontType="18Medium" className={classes.heading}>
            Basic setup
          </BaseTypography>
          <BaseTextField
            className={classes.field}
            autoFocus
            fullWidth
            label={
              <>
                <BaseTypography
                  className={classes.nameDescription}
                  fontType="13Medium"
                >
                  Name
                </BaseTypography>
                <BaseTypography className={classes.nameDescription}>
                  &nbsp;- How it should appear in the sidebar
                </BaseTypography>
              </>
            }
            variant="outlined"
            sizeVariant="medium"
            name="name"
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            error={Boolean(touched?.name && errors?.name)}
            helperText={touched && touched.name && errors && errors.name}
          />

          <BaseTypography fontType="13Medium">Sidebar icon</BaseTypography>
          <SuggestIconPicker
            value={values.icon}
            onChange={(iconName) => setFieldValue('icon', iconName)}
            appName={values.name}
          />
        </section>
        {!isAppBuilderApp && (
          <>
            <section className={classes.section}>
              <BaseTypography fontType="18Medium" className={classes.heading}>
                Configure views
              </BaseTypography>
              <BaseTextField
                className={classes.field}
                sizeVariant="medium"
                select
                fullWidth
                variant="outlined"
                onChange={(e) => {
                  if (e.target.value === 'internal') {
                    setFieldValue('clientUrl', '');
                  }
                  handleChange(e);
                }}
                label="Setup type"
                onBlur={handleBlur}
                name="visibility"
                defaultValue="global"
                value={values.visibility}
              >
                <MenuItem value="client">
                  {t(($) => $.page_apps.client_app_visibility_label)}
                </MenuItem>
                <MenuItem value="internal">
                  {t(($) => $.page_apps.internal_app_visibility_label)}
                </MenuItem>
              </BaseTextField>

              {values.visibility === 'client' && (
                <BaseTextField
                  className={classes.field}
                  fullWidth
                  label={
                    <>
                      <BaseTypography
                        className={classes.nameDescription}
                        fontType="13Medium"
                      >
                        {t(($) => $.page_apps.client_facing_url.title)}
                      </BaseTypography>
                      <BaseTypography className={classes.nameDescription}>
                        {t(($) => $.page_apps.client_facing_url.description)}
                      </BaseTypography>
                    </>
                  }
                  variant="outlined"
                  sizeVariant="medium"
                  name="clientUrl"
                  value={values.clientUrl}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(touched?.clientUrl && errors?.clientUrl)}
                  helperText={touched?.clientUrl && errors?.clientUrl}
                />
              )}

              <BaseTextField
                className={classes.field}
                fullWidth
                label={
                  <>
                    <BaseTypography
                      className={classes.nameDescription}
                      fontType="13Medium"
                    >
                      {t(($) => $.page_apps.internal_facing_url.title)}
                    </BaseTypography>
                    <BaseTypography className={classes.nameDescription}>
                      {t(($) => $.page_apps.internal_facing_url.description)}
                    </BaseTypography>
                  </>
                }
                variant="outlined"
                sizeVariant="medium"
                name="internalUrl"
                value={values.internalUrl}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(touched?.internalUrl && errors?.internalUrl)}
                helperText={touched?.internalUrl && errors?.internalUrl}
              />
              <BaseTextField
                className={classes.field}
                fullWidth
                label={
                  <>
                    <BaseTypography
                      className={classes.nameDescription}
                      fontType="13Medium"
                    >
                      {t(($) => $.page_apps.notification_url.title)}
                    </BaseTypography>
                    <BaseTypography className={classes.nameDescription}>
                      {t(($) => $.page_apps.notification_url.description)}
                    </BaseTypography>
                  </>
                }
                variant="outlined"
                sizeVariant="medium"
                name="notificationUrl"
                value={values.notificationUrl}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(
                  touched?.notificationUrl && errors?.notificationUrl,
                )}
                helperText={touched?.notificationUrl && errors?.notificationUrl}
              />
            </section>
            <section className={classes.section}>
              <URLQueryParamsSection
                queryParams={values.queryParams}
                content={
                  values.visibility === 'client'
                    ? values.clientUrl
                    : values.internalUrl
                }
                setFieldValue={setFieldValue}
              />
            </section>
            <section className={classes.section}>
              <BaseTypography fontType="18Medium" className={classes.heading}>
                Configure webhooks
              </BaseTypography>

              <div className={classes.field}>
                <SettingsToggleCard
                  title="Enable webhooks"
                  description="When enabled, you can specify a webhook endpoint URL and select specific webhooks to subscribe to"
                  fieldName="enableWebhooks"
                  checked={values.enableWebhooks}
                />
              </div>

              {values.enableWebhooks && (
                <>
                  <BaseTextField
                    className={classes.field}
                    fullWidth
                    label={
                      <>
                        <BaseTypography
                          className={classes.nameDescription}
                          fontType="13Medium"
                        >
                          Webhook Endpoint URL
                        </BaseTypography>
                      </>
                    }
                    variant="outlined"
                    sizeVariant="medium"
                    name="webhookUrl"
                    value={values.webhookUrl}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(touched?.webhookUrl && errors?.webhookUrl)}
                    helperText={touched?.webhookUrl && errors?.webhookUrl}
                    type="URL"
                  />

                  <ComboBox
                    textFieldVariant="medium"
                    error={Boolean(
                      touched.webhookEvents && errors.webhookEvents,
                    )}
                    helperText={touched.webhookEvents && errors.webhookEvents}
                    id="events"
                    label="Events to send"
                    placeholder="Select events..."
                    onBlur={handleBlur}
                    onChange={(_, vals) => {
                      setFieldValue(
                        'webhookEvents',
                        vals.map((val) => val.id),
                      );
                    }}
                    values={values.webhookEvents.map((event) => ({
                      label: event,
                      id: event,
                      type: 'text',
                    }))}
                    options={WEBHOOK_EVENTS.map((eventName) => ({
                      label: eventName,
                      id: eventName,
                      type: 'text',
                    }))}
                    showPlaceholder
                    multiple
                  />
                </>
              )}
            </section>
          </>
        )}
        {EnableMarketplaceAppNotificationSettings && (
          <section className={classes.section}>
            <NotificationSettingsSection />
          </section>
        )}
        {values.visibility === 'client' && (
          <section>
            {isAddingNewApp ? (
              <AppVisibilityConfigSetting
                scenario={VisibilityScenario.AddAnApp}
                onVisibilityConfigSaved={(visibilityRules) => {
                  setFieldValue('visibilityConfig', visibilityRules);
                }}
              />
            ) : (
              <AppVisibilityConfigSetting
                scenario={VisibilityScenario.EditApp}
              />
            )}
          </section>
        )}
        {!isAddingNewApp && !isAppBuilderApp && (
          <section className={classes.section}>
            <DuplicateAppInstance />
          </section>
        )}
        {!isAddingNewApp && (
          <section className={classes.removeApp}>
            <RemoveApp />
          </section>
        )}
      </>
    );
  };
  return {
    FormRenderer,
    validationScheme: toFormikValidationSchema(schema),
  };
};

export function SetupCustomApp() {
  const { portalHeader: portalId } = React.useContext(PortalConfigContext);
  const { query } = React.useContext(RouteContext);
  const modalCtx = React.useContext(AppSettingsModalContext);
  const {
    EnableAppSetupImprovements,
    EnableMarketplaceAppNotificationSettings,
  } = useFeatureFlags();
  const { moduleId: moduleIdQueryParam, appId: appIdQueryParam } =
    query as SetupModulePageQueryParams;
  const { folderId, addAppReturnPath } = useAppSetupPaths();
  // Pre-generate the new install's extension config id so that, behind the
  // flag, we can deep-link to the created app's page instead of the App Library.
  const newModuleConfigId = React.useMemo(() => v4(), []);
  const [createApp, createAppResult] = useCreateAppMutation();
  const [updateApp, updateAppResult] = useUpdateAppMutation();
  const hasEmbedUrl =
    createAppResult.isSuccess && Boolean(createAppResult.data?.internalUrl);
  const { isSuccess, initialFormValue } = useInitialValues(
    appIdQueryParam,
    moduleIdQueryParam,
  );
  // We wanted to enable creating apps with no URLs to facilitate
  // creating your API key before you deploy your app. That necessitates
  // fallback done states, sometimes we send folks to their app, sometimes
  // we send them to the app setup page.
  const handleDone = () => {
    if (modalCtx) {
      modalCtx.onClose();
    } else {
      history.push(
        EnableAppSetupImprovements
          ? `${CLIENT_APPS_PAGE.path}?id=${newModuleConfigId}`
          : APP_SETUP_PAGE.path,
      );
    }
  };

  const handleCancel = () => {
    // cancel add custom app should redirect back to where the flow was launched
    // from (the redesigned Add App flow) or the legacy add app page.
    if (moduleIdQueryParam === 'new') {
      if (modalCtx) {
        modalCtx.onClose();
      } else {
        history.push(addAppReturnPath);
      }
    }
  };

  React.useEffect(() => {
    if (updateAppResult.isSuccess) {
      if (modalCtx) {
        modalCtx.onClose();
      } else {
        history.push(APP_SETUP_PAGE.path);
      }
    }
  }, [updateAppResult, modalCtx]);

  if (!isSuccess) return null;

  return (
    <ModuleContainer description={<SetupModulePageDescription />}>
      <ModalWrapper
        noCancelButton
        width={480}
        onClose={handleDone}
        onSuccess={handleDone}
        open={createAppResult.isSuccess}
        successButtonLabel={
          // Behind app-setup improvements, success always deep-links to the
          // created app's page, so the label is "Go to App" regardless of
          // whether an embed URL was provided.
          EnableAppSetupImprovements
            ? 'Go to App'
            : hasEmbedUrl
              ? 'Go to App'
              : 'Done'
        }
      >
        {createAppResult.isSuccess && createAppResult.data?.apiKey && (
          <>
            <BaseTypography fontType="18Medium">
              Your app has been created
            </BaseTypography>
            <ApiKey apiKey={createAppResult.data.apiKey} />
          </>
        )}
      </ModalWrapper>
      <SettingsPageForm
        BreadcrumbsComponent={<SetupAppBreadCrumbs />}
        initialFormValue={initialFormValue}
        useFormHook={useForm}
        validateOnMount={moduleIdQueryParam === 'new'}
        handleSave={async (values): Promise<boolean | void> => {
          // Always drop notificationConfig from the base spread so omit's return type stays
          // precise (all literal keys); re-add it only when the authoring UI is enabled.
          const appValues = omit(
            values,
            'icon',
            'visibility',
            'queryParams',
            'notificationConfig',
          );
          // Persist the action label only when all three parts are filled, so we never store a
          // partial or whitespace-only "[verb] [noun]" label.
          const actionLabel = values.notificationConfig?.actionLabel;
          const completeActionLabel =
            actionLabel?.verb?.trim() &&
            actionLabel.singularNoun?.trim() &&
            actionLabel.pluralNoun?.trim()
              ? {
                  verb: actionLabel.verb.trim(),
                  singularNoun: actionLabel.singularNoun.trim(),
                  pluralNoun: actionLabel.pluralNoun.trim(),
                }
              : undefined;
          const notificationConfigPayload =
            EnableMarketplaceAppNotificationSettings
              ? {
                  notificationConfig: {
                    notifications:
                      values.notificationConfig?.notifications ?? [],
                    ...(completeActionLabel
                      ? { actionLabel: completeActionLabel }
                      : {}),
                  },
                }
              : {};
          if (moduleIdQueryParam === 'new') {
            const payload: Parameters<typeof createApp>[0] = {
              ...appValues,
              ...notificationConfigPayload,
              clientUrl: values.clientUrl ?? '',
              internalUrl: values.internalUrl ?? '',
              notificationUrl: values.notificationUrl ?? '',
              webhookUrl: values.webhookUrl ?? '',
              portalId,
              icon: values.icon,
              visibilityConfig: values.visibilityConfig,
              folderId,
              queryParams: values.queryParams,
              moduleConfigId: newModuleConfigId,
            };
            await createApp(payload);
            // Prevent auto-close so the API key dialog can render
            if (modalCtx) return false;
          } else if (appIdQueryParam) {
            const payload: Parameters<typeof updateApp>[0] = {
              id: appIdQueryParam,
              ...appValues,
              ...notificationConfigPayload,
              clientUrl: values.clientUrl ?? '',
              internalUrl: values.internalUrl ?? '',
              notificationUrl: values.notificationUrl ?? '',
              webhookUrl: values.webhookUrl ?? '',
              portalId,
              moduleId: moduleIdQueryParam,
              icon: values.icon,
              queryParams: values.queryParams,
            };
            await updateApp(payload);
          }
        }}
        actionLabel={moduleIdQueryParam === 'new' ? 'Add' : 'Save'}
        handleCancel={handleCancel}
        showConfirmationModal={false}
        shouldShowPageAction={moduleIdQueryParam === 'new' ? true : undefined}
        enableDefaultErrorTooltip
      />
    </ModuleContainer>
  );
}
